<template>
  <div>
    <!-- Sub-Tabs für Tiers -->
    <v-tabs v-model="tierTab" background-color="transparent" dark color="white" class="mb-6 tier-tabs" centered>
      <v-tab class="bronze-tab"><v-icon left small>mdi-medal-outline</v-icon> Bronze</v-tab>
      <v-tab class="silber-tab"><v-icon left small>mdi-medal-outline</v-icon> Silber</v-tab>
      <v-tab class="gold-tab"><v-icon left small>mdi-medal</v-icon> Gold</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tierTab" class="transparent-bg">
      <v-tab-item v-for="(tier, index) in ['bronze', 'silber', 'gold']" :key="index">
        <v-row v-if="filteredModules(tier).length > 0">
          <v-col cols="12" sm="6" md="4" lg="3" v-for="(mod, idx) in filteredModules(tier)" :key="mod._id">
            <v-card
              class="module-card rounded-xl overflow-hidden h-100"
              dark
              :class="{ locked: !isTierAccessible(tier), completed: getModuleProgress(mod._id) === 100 }"
              :style="{ animationDelay: idx * 0.05 + 's' }"
              @click="openModule(mod, tier)">
              <!-- Visual Header -->
              <div class="card-visual" :class="`visual-${tier}`">
                <div class="card-visual-pattern"></div>
                <div class="card-visual-content">
                  <v-icon size="48" :color="tierColor(tier)" class="visual-icon">{{ mod.icon || "mdi-school" }}</v-icon>
                  <v-chip :color="tierColor(tier)" text-color="black" x-small class="tier-chip font-weight-bold">{{ tier.toUpperCase() }}</v-chip>
                </div>

                <!-- Completed Stamp -->
                <div v-if="getModuleProgress(mod._id) === 100" class="completed-stamp">
                  <v-icon color="#D4AF37">mdi-check-decagram</v-icon>
                </div>

                <!-- Lock Overlay -->
                <div class="lock-overlay d-flex align-center justify-center" v-if="!isTierAccessible(tier)">
                  <v-icon size="48" color="white">mdi-lock</v-icon>
                  <span class="mt-2 text-caption font-weight-bold">Vorherige Stufe nötig</span>
                </div>
              </div>

              <v-card-text class="pt-4 pb-3">
                <h3 class="module-title text-h6 font-weight-bold white--text mb-1 text-truncate" :title="mod.title">{{ mod.title }}</h3>
                <p class="module-desc text-caption grey--text mb-3">{{ mod.description || "Keine Beschreibung" }}</p>

                <div class="d-flex align-center grey--text text-caption mb-3">
                  <v-icon small color="grey" class="mr-1">mdi-play-circle-outline</v-icon>
                  <span>{{ (mod.lessons && mod.lessons.length) || 0 }}</span>
                  <span class="mx-2">·</span>
                  <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                  <span>{{ mod.estimatedMinutes || 0 }} Min</span>
                </div>

                <!-- Progress -->
                <div v-if="isTierAccessible(tier)" class="progress-row d-flex align-center">
                  <v-progress-linear
                    :value="getModuleProgress(mod._id)"
                    color="#D4AF37"
                    height="6"
                    rounded
                    background-color="rgba(255,255,255,0.08)"
                    class="flex-grow-1"></v-progress-linear>
                  <span class="progress-label ml-3">{{ getModuleProgress(mod._id) }}<small>%</small></span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col class="text-center py-12">
            <v-icon size="64" color="grey darken-3">mdi-bookshelf</v-icon>
            <h3 class="text-h6 grey--text mt-4">Keine Module in dieser Stufe verfügbar</h3>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "LearningSuite",
  data() {
    return {
      tierTab: 0,
      modules: [],
      progresses: [],
      myLicenses: [],
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const [resMod, resProg, resLic] = await Promise.all([
          api.akademie.fetchModules(),
          api.akademie.fetchProgress(),
          api.akademie.fetchMyLicenses(),
        ])
        this.modules = resMod || []
        this.progresses = resProg || []
        this.myLicenses = resLic || []
      } catch (err) {
        console.error("Fehler beim Laden der Learning Suite Daten:", err)
      }
    },
    filteredModules(tier) {
      // Nur aktive Module für den User-View (Admin sieht via fetchModules auch inaktive zurück, aber im Lernbereich blenden wir die aus)
      return this.modules.filter((m) => m.tier === tier && m.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    isTierAccessible(tier) {
      if (tier === "bronze") return true
      if (tier === "silber") return this.myLicenses.some((l) => l.tier === "bronze" && l.status === "active")
      if (tier === "gold") return this.myLicenses.some((l) => l.tier === "silber" && l.status === "active")
      return false
    },
    getModuleProgress(moduleId) {
      const prog = this.progresses.find((p) => p.module === moduleId || (p.module && p.module._id === moduleId))
      if (!prog) return 0
      if (prog.completedAt) return 100
      const mod = this.modules.find((m) => m._id === moduleId)
      if (!mod || !mod.lessons) return 0
      const completedLessons = prog.lessons ? prog.lessons.filter((l) => l.completedAt != null).length : 0
      return Math.floor((completedLessons / (mod.lessons.length + 1)) * 100)
    },
    tierColor(tier) {
      if (tier === "bronze") return "#CD7F32"
      if (tier === "silber") return "#C0C0C0"
      if (tier === "gold") return "#FFD700"
      return "grey"
    },
    openModule(mod, tier) {
      if (!this.isTierAccessible(tier)) return
      this.$router.push(`/akademie/modules/${mod._id}`)
    },
  },
}
</script>

<style scoped>
.transparent-bg { background-color: transparent !important; }

.tier-tabs :deep(.v-tab) {
  font-weight: bold;
  letter-spacing: 1.5px;
  border-radius: 30px;
  margin: 0 6px;
  transition: all 0.3s ease;
  text-transform: none;
}
.bronze-tab.v-tab--active { background-color: rgba(205, 127, 50, 0.18); color: #cd7f32 !important; }
.silber-tab.v-tab--active { background-color: rgba(192, 192, 192, 0.18); color: #c0c0c0 !important; }
.gold-tab.v-tab--active { background-color: rgba(255, 215, 0, 0.18); color: #ffd700 !important; }

.module-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  animation: cardIn 0.5s ease both;
}
@keyframes cardIn {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

.module-card:hover:not(.locked) {
  transform: translateY(-6px);
  border-color: rgba(212, 175, 55, 0.5);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.5), 0 0 18px rgba(212, 175, 55, 0.18);
}

.module-card.locked {
  cursor: not-allowed;
  opacity: 0.75;
}

.module-card.completed {
  border-color: rgba(212, 175, 55, 0.35);
}

/* Card Visual */
.card-visual {
  position: relative;
  height: 130px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.visual-bronze { background: radial-gradient(circle at 30% 30%, rgba(205, 127, 50, 0.25), rgba(20, 20, 20, 0.9)); }
.visual-silber { background: radial-gradient(circle at 30% 30%, rgba(192, 192, 192, 0.2), rgba(20, 20, 20, 0.9)); }
.visual-gold { background: radial-gradient(circle at 30% 30%, rgba(255, 215, 0, 0.22), rgba(20, 20, 20, 0.92)); }

.card-visual-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0);
  background-size: 12px 12px;
  opacity: 0.6;
  pointer-events: none;
}

.card-visual-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.visual-icon {
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6));
  transition: transform 0.4s ease;
}
.module-card:hover:not(.locked) .visual-icon {
  transform: scale(1.12) rotate(-4deg);
}

.tier-chip {
  letter-spacing: 1px;
}

.completed-stamp {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 999px;
  padding: 4px;
  backdrop-filter: blur(4px);
  animation: stampIn 0.6s ease both;
}
@keyframes stampIn {
  0% { opacity: 0; transform: rotate(-20deg) scale(0.5); }
  100% { opacity: 1; transform: rotate(0) scale(1); }
}

.lock-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  flex-direction: column;
  z-index: 2;
  color: white;
}

.module-title {
  line-height: 1.3;
}

.module-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.5em;
}

.progress-row {
  white-space: nowrap;
}
.progress-label {
  color: #d4af37;
  font-weight: 700;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
}
.progress-label small {
  font-size: 0.7rem;
  opacity: 0.8;
}

.h-100 { height: 100%; }
</style>
